import { ChangeDetectionStrategy, Component, Input } from '@angular/core'

import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs'

import { RxStateComponent } from '@alliance/shared/models'
import { MediaService } from '@alliance/shared/utils'
import { SkeletonClubsPostsListDesktop, SkeletonClubsPostsListMobile } from '@alliance/shared/icons'

import { ClubPost, DEFAULT_SORTING, PostsListService, PublicClubPostsCursorPaginationInput, PublicClubPostsFilterInput, PublicClubPostsSortingEnum } from '@alliance/clubs/domain'
import { RoutingService } from '@alliance/clubs/utils'

import { Translations } from '../../localization/translations'

@Component({
  selector: 'alliance-clubs-posts-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.tw.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostsListComponent extends RxStateComponent<{
  data: { posts: ClubPost[] | null; count: number }
  posts: ClubPost[] | null
  postsCount: number
  filters: PublicClubPostsFilterInput | null
  sorting: PublicClubPostsSortingEnum | null
  cursorPagination: PublicClubPostsCursorPaginationInput | null
  noMoreResults: boolean
  isMobileScreen: boolean
  postCardClasses: string | null
  loadMorePostsInProgress: boolean
  withCount: boolean
  searchEmptyState: boolean
  withSorting: boolean
  firstPostsWithSortingLoaded: boolean
}> {
  public state = this.connectViewState([
    'posts',
    'postsCount',
    'isMobileScreen',
    'postCardClasses',
    'loadMorePostsInProgress',
    'withCount',
    'searchEmptyState',
    'withSorting',
    'sorting',
    'firstPostsWithSortingLoaded'
  ])
  public readonly SkeletonClubsPostsListDesktop = SkeletonClubsPostsListDesktop
  public readonly SkeletonClubsPostsListMobile = SkeletonClubsPostsListMobile
  public readonly defaultSorting = DEFAULT_SORTING

  public constructor(private postsListService: PostsListService, private mediaService: MediaService, public translations: Translations, private routingService: RoutingService) {
    super()

    this.initState({
      data: combineLatest([this.select('filters'), this.select('sorting')]).pipe(
        filter(([filters, sorting]) => !!filters && !!sorting),
        tap(() => {
          this.set({ posts: null, cursorPagination: null })
        }),
        switchMap(([filters, sorting]) => this.postsListService.getPostsList$(filters ?? {}, sorting).pipe(filter(data => !!data.posts)))
      ),
      posts: this.select('data').pipe(
        filter(data => !!data.posts),
        map(({ posts }) => posts)
      ),
      postsCount: this.select('data').pipe(map(({ count }) => count)),
      filters: null,
      sorting: null,
      cursorPagination: null,
      noMoreResults: combineLatest([this.select('postsCount'), this.select('posts')]).pipe(
        map(([postsCount]) => {
          const { posts } = this.get()
          return (posts?.length ?? 0) >= postsCount
        })
      ),
      isMobileScreen: this.mediaService.select('isMobileScreen'),
      postCardClasses: null,
      loadMorePostsInProgress: false,
      withCount: false,
      searchEmptyState: false,
      withSorting: false,
      firstPostsWithSortingLoaded: combineLatest([this.select('withSorting'), this.select('posts')]).pipe(
        filter(([withSorting, posts]) => !!withSorting && !!posts),
        take(1),
        map(data => !!data)
      )
    })

    this.hold(this.select('cursorPagination').pipe(filter(cursorPagination => !!cursorPagination)), cursorPagination => {
      this.set({ loadMorePostsInProgress: true })
      return this.hold(this.postsListService.getPostsList$(this.get().filters ?? {}, this.get().sorting, cursorPagination ?? undefined), data => {
        const { posts } = this.get()
        this.set({
          posts: [...(posts ?? []), ...(data?.posts ?? [])],
          loadMorePostsInProgress: false
        })
      })
    })
  }

  @Input()
  public set filters(value: PublicClubPostsFilterInput) {
    this.set({ filters: value })
  }
  @Input()
  public set sorting(value: PublicClubPostsSortingEnum) {
    this.set({ sorting: value })
  }
  @Input()
  public set postCardClasses(value: string) {
    this.set({ postCardClasses: value })
  }
  @Input()
  public set withCount(value: boolean) {
    this.set({ withCount: value })
  }
  @Input()
  public set searchEmptyState(value: boolean) {
    this.set({ searchEmptyState: value })
  }
  @Input()
  public set withSorting(value: boolean) {
    this.set({ withSorting: value })
  }

  public loadMore(): void {
    const { noMoreResults, posts, loadMorePostsInProgress } = this.get()
    if (!noMoreResults && posts?.length && !loadMorePostsInProgress) {
      this.set({
        cursorPagination: {
          before: `${posts?.length}`,
          take: 10
        }
      })
    }
  }

  public getPostPageRouterLink(post: ClubPost | null): string {
    if (!post || !post?.topics[0]?.engname || !post?.slug) {
      return ''
    }

    const params = {
      topicName: post?.topics[0].engname,
      postSlug: post?.slug
    }
    return this.routingService.getPostPageRouterLink(params)
  }

  public setSorting(sorting: PublicClubPostsSortingEnum | null): void {
    if (sorting) {
      this.set({ sorting })
    }
  }

  public trackByFn(index: number): number {
    return index
  }
}
